/* eslint-disable no-mixed-operators */
import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/layout";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { Table, Row, Col, Input, Modal, TimePicker, Button, Form, Select, message, Typography, Avatar } from "antd";
import toast from "react-hot-toast";
import moment from "moment";
import {
  SearchOutlined, ClearOutlined, EditOutlined, CheckCircleOutlined,
  StopOutlined, UserOutlined, FilterOutlined, TeamOutlined, ClockCircleOutlined,
  EnvironmentOutlined, MedicineBoxOutlined, PhoneOutlined, MailOutlined,
   MoneyCollectFilled
} from "@ant-design/icons";

const { Option } = Select;
const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("doctors-list-styles")) return;
  const el = document.createElement("style");
  el.id = "doctors-list-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .dl-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .dl-wrap { max-width: 1200px; margin: 0 auto; padding: 32px 24px 64px; }

    /* Hero */
    .dl-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 28px;
    }
    .dl-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .dl-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -40px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }
    .dl-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }
    .dl-hero-badge {
      background: rgba(255,255,255,0.12); border-radius: 20px;
      padding: 4px 12px; font-size: 12px; color: rgba(255,255,255,0.75);
      font-family: 'DM Sans', sans-serif; display: inline-block; margin-top: 12px;
    }

    /* Toolbar */
    .dl-toolbar {
      background: ${WHITE}; border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 16px 20px; margin-bottom: 20px;
      display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    }

    /* Search input */
    .dl-root .dl-search.ant-input-affix-wrapper {
      border-radius: 10px !important; height: 40px !important;
      border: 1.5px solid #e2e8f0 !important; flex: 1; min-width: 200px;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .dl-root .dl-search.ant-input-affix-wrapper:hover,
    .dl-root .dl-search.ant-input-affix-wrapper-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .dl-root .dl-search .ant-input { font-family: 'DM Sans', sans-serif !important; font-size: 13px !important; }

    /* Toolbar buttons */
    .dl-btn-outline {
      height: 40px !important; padding: 0 18px !important;
      border-radius: 10px !important; font-size: 13px !important;
      font-family: 'DM Sans', sans-serif !important; font-weight: 600 !important;
      border: 1.5px solid #e2e8f0 !important; background: ${WHITE} !important;
      color: ${SLATE_MID} !important; transition: border-color .2s, color .2s !important;
    }
    .dl-btn-outline:hover {
      border-color: ${TEAL} !important; color: ${TEAL} !important;
    }

    /* Table card */
    .dl-table-wrap {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06); overflow: hidden;
    }

    /* Ant table overrides */
    .dl-root .ant-table-thead > tr > th {
      background: ${SURFACE} !important; color: ${SLATE_MID} !important;
      font-size: 11px !important; font-weight: 700 !important;
      letter-spacing: .5px; text-transform: uppercase;
      border-bottom: 1.5px solid #f1f5f9 !important;
      font-family: 'Sora', sans-serif !important; padding: 14px 16px !important;
    }
    .dl-root .ant-table-tbody > tr > td {
      font-size: 13px !important; font-family: 'DM Sans', sans-serif !important;
      color: ${SLATE_MID} !important; padding: 14px 16px !important;
      border-bottom: 1px solid #f8fafc !important;
    }
    .dl-root .ant-table-tbody > tr:hover > td {
      background: #f0fdfb !important;
    }
    .dl-root .ant-pagination { padding: 16px !important; }
    .dl-root .ant-pagination-item-active { border-color: ${TEAL} !important; }
    .dl-root .ant-pagination-item-active a { color: ${TEAL} !important; }

    /* Status badges */
    .dl-badge {
      padding: 3px 10px; border-radius: 20px;
      font-size: 11px; font-weight: 700; font-family: 'Sora', sans-serif;
      display: inline-block;
    }
    .dl-badge-approved  { background: ${TEAL_LIGHT}; color: ${EMERALD}; }
    .dl-badge-pending   { background: #fef9c3; color: #b45309; }
    .dl-badge-disabled  { background: #fee2e2; color: #dc2626; }
    .dl-badge-blocked   { background: #fee2e2; color: #dc2626; }

    /* Action buttons in table */
    .dl-action-btn {
      height: 30px !important; padding: 0 12px !important;
      border-radius: 8px !important; font-size: 12px !important;
      font-family: 'DM Sans', sans-serif !important; font-weight: 600 !important;
      border: 1.5px solid !important; transition: all .2s !important;
    }
    .dl-action-approve { border-color: ${TEAL} !important; color: ${TEAL} !important; background: ${WHITE} !important; }
    .dl-action-approve:hover { background: ${TEAL} !important; color: ${WHITE} !important; }
    .dl-action-edit    { border-color: #6366f1 !important; color: #6366f1 !important; background: ${WHITE} !important; }
    .dl-action-edit:hover { background: #6366f1 !important; color: ${WHITE} !important; }
    .dl-action-enable  { border-color: ${EMERALD} !important; color: ${EMERALD} !important; background: ${WHITE} !important; }
    .dl-action-enable:hover { background: ${EMERALD} !important; color: ${WHITE} !important; }

    /* Clinic day chips */
    .dl-day-chip {
      display: inline-block; padding: 2px 8px; margin: 2px 2px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      border-radius: 6px; font-size: 11px; font-weight: 600;
    }

    /* ── Modal ── */
    .dl-modal .ant-modal-content { border-radius: 20px !important; overflow: hidden; padding: 0 !important; }
    .dl-modal .ant-modal-header {
      background: linear-gradient(135deg, ${SLATE}, ${SLATE_MID}) !important;
      padding: 20px 28px !important; border-radius: 0 !important; margin: 0 !important;
    }
    .dl-modal .ant-modal-title {
      color: ${WHITE} !important; font-family: 'Sora', sans-serif !important;
      font-weight: 700 !important; font-size: 16px !important;
    }
    .dl-modal .ant-modal-close { color: ${MUTED} !important; top: 18px !important; }
    .dl-modal .ant-modal-close:hover { color: ${WHITE} !important; }
    .dl-modal .ant-modal-body { padding: 24px 28px !important; }
    .dl-modal .ant-modal-footer { padding: 16px 28px !important; border-top: 1.5px solid #f1f5f9 !important; }

    /* Modal form fields */
    .dl-modal .ant-form-item { margin-bottom: 16px !important; }
    .dl-modal .ant-form-item-label { padding-bottom: 4px !important; }
    .dl-modal .ant-form-item-label > label {
      font-size: 12px !important; font-weight: 600 !important;
      color: ${SLATE_MID} !important; height: 20px !important;
    }
    .dl-modal .ant-input,
    .dl-modal .ant-input-affix-wrapper {
      border-radius: 10px !important; height: 40px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      transition: border-color .2s !important;
    }
    .dl-modal .ant-input-affix-wrapper .ant-input { height: auto !important; border: none !important; box-shadow: none !important; }
    .dl-modal .ant-input:hover, .dl-modal .ant-input:focus,
    .dl-modal .ant-input-affix-wrapper:hover, .dl-modal .ant-input-affix-wrapper-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .dl-modal .ant-input[disabled] { background: ${SURFACE} !important; color: ${MUTED} !important; }
    .dl-modal .ant-select-selector {
      border-radius: 10px !important; height: 40px !important;
      border: 1.5px solid #e2e8f0 !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      transition: border-color .2s !important;
    }
    .dl-modal .ant-select-selection-item,
    .dl-modal .ant-select-selection-placeholder { line-height: 38px !important; }
    .dl-modal .ant-select-focused .ant-select-selector,
    .dl-modal .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .dl-modal .ant-select-multiple .ant-select-selector {
      height: auto !important; min-height: 40px !important; padding: 3px 8px !important;
    }
    .dl-modal .ant-picker {
      border-radius: 10px !important; height: 40px !important;
      border: 1.5px solid #e2e8f0 !important; width: 100% !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
      transition: border-color .2s !important;
    }
    .dl-modal .ant-picker:hover, .dl-modal .ant-picker-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Modal footer buttons */
    .dl-modal-cancel {
      height: 40px !important; padding: 0 20px !important; border-radius: 10px !important;
      font-size: 13px !important; font-weight: 600 !important;
      font-family: 'DM Sans', sans-serif !important;
      border: 1.5px solid #e2e8f0 !important; color: ${SLATE_MID} !important;
    }
    .dl-modal-update {
      height: 40px !important; padding: 0 24px !important; border-radius: 10px !important;
      font-size: 13px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; color: ${WHITE} !important;
      box-shadow: 0 4px 12px ${TEAL}44 !important;
    }

    /* Disable / Enable strip inside modal */
    .dl-status-strip {
      border-radius: 12px; padding: 12px 16px; margin-bottom: 20px;
      display: flex; align-items: center; justify-content: space-between;
    }
    .dl-status-strip.active   { background: ${TEAL_LIGHT}; }
    .dl-status-strip.inactive { background: #fee2e2; }
    .dl-strip-btn {
      height: 34px !important; padding: 0 16px !important; border-radius: 8px !important;
      font-size: 12px !important; font-weight: 700 !important; border: none !important;
      font-family: 'Sora', sans-serif !important; cursor: pointer !important;
    }
    .dl-strip-btn.disable { background: #dc2626 !important; color: ${WHITE} !important; }
    .dl-strip-btn.enable  { background: ${EMERALD} !important; color: ${WHITE} !important; }

    /* Animations */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .dl-fade   { animation: fadeUp .5s ease both; }
    .dl-fade-1 { animation-delay: .05s; }
    .dl-fade-2 { animation-delay: .12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ──────────────────────────────────────────── */
function Doctorslist() {
  const dispatch = useDispatch();

  const [doctors,        setDoctors]        = useState([]);
  const [services,       setServices]       = useState([]);
  const [clinicDays,     setClinicDays]     = useState([]);
  const [locations,      setLocations]      = useState([]);
  const [searchTerm,     setSearchTerm]     = useState("");
  const [filteredInfo,   setFilteredInfo]   = useState({});
  const [sortedInfo,     setSortedInfo]     = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLoading,   setModalLoading]   = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    axios.get("/api/user/get-all-clinic-day")
      .then(res => { if (res.data.success) setClinicDays(res.data.clinicDays); })
      .catch(() => message.error("Error fetching clinic days"));
  }, []);

  useEffect(() => {
    axios.get("/api/user/get-all-location", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then(res => { if (res.data.success) setLocations(res.data.data); })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axios.get("/api/user/get-all-services", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then(res => { if (res.data.success) setServices(res.data.data); })
      .catch(console.error);
  }, []);

  const getDoctorData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/admin/get-all-doctors", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) setDoctors(res.data.data);
    } catch { dispatch(hideLoading()); }
  }, [dispatch]);

  useEffect(() => { getDoctorData(); }, [getDoctorData]);

  const changeDoctorStatus = async (record, status) => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/admin/change-doctor-status",
        { doctorId: record._id, userId: record.userId, status },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) { toast.success(res.data.message); getDoctorData(); }
    } catch { toast.error("Something went wrong"); dispatch(hideLoading()); }
  };

  const openEditModal = (record) => {
    setSelectedDoctor(record);
    form.setFieldsValue({
      clinicDay: record.clinicDay || [],
      feePerConsultation: record.feePerConsultation,
      service: record.service || "",
      email: record.email || "",
      appointmentTime: record.appointmentTime
        ? [moment(record.appointmentTime[0], "HH:mm"), moment(record.appointmentTime[1], "HH:mm")]
        : [],
      phoneNumber: record.phoneNumber || "",
      location: record.location || "",
    });
    setIsModalVisible(true);
  };

  const handleDisable = () => { changeDoctorStatus(selectedDoctor, "disabled"); setIsModalVisible(false); };
  const handleEnable  = () => { changeDoctorStatus(selectedDoctor, "approved"); setIsModalVisible(false); };

  const handleEditSubmit = async () => {
    try {
      setModalLoading(true);
      const values = await form.validateFields();
      const res = await axios.post(
        "/api/admin/update-doctor-clinic",
        {
          doctorId: selectedDoctor._id,
          clinicDay: values.clinicDay,
          feePerConsultation: values.feePerConsultation,
          service: values.service,
          appointmentTime: values.appointmentTime ? values.appointmentTime.map(t => t.format("HH:mm")) : [],
          phoneNumber: values.phoneNumber,
          location: values.location,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setModalLoading(false);
      if (res.data.success) {
        toast.success("Doctor profile updated successfully");
        setIsModalVisible(false);
        getDoctorData();
      } else { toast.error(res.data.message); }
    } catch { setModalLoading(false); toast.error("Error updating doctor profile"); }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  /* ── Columns ── */
  const columns = [
    {
      title: "Doctor",
      dataIndex: "firstName",
      key: "name",
      filteredValue: filteredInfo.name || null,
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
      sortOrder: sortedInfo.columnKey === "name" ? sortedInfo.order : null,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={34} style={{ background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
            {record.firstName?.[0]?.toUpperCase()}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 13, color: SLATE, display: "block" }}>
              {record.firstName} {record.lastName}
            </Text>
            <Text style={{ fontSize: 11, color: MUTED }}>{record.email}</Text>
          </div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      sorter: (a, b) => (a.phoneNumber || "").localeCompare(b.phoneNumber || ""),
      sortOrder: sortedInfo.columnKey === "phoneNumber" ? sortedInfo.order : null,
      render: text => <Text style={{ fontSize: 13, color: SLATE_MID }}>{text || "—"}</Text>,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      sorter: (a, b) => (a.location || "").localeCompare(b.location || ""),
      sortOrder: sortedInfo.columnKey === "location" ? sortedInfo.order : null,
      render: text => <Text style={{ fontSize: 13, color: SLATE_MID }}>{text || "—"}</Text>,
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      sorter: (a, b) => (a.service || "").localeCompare(b.service || ""),
      sortOrder: sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      render: text => <Text style={{ fontSize: 13, color: SLATE_MID }}>{text || "—"}</Text>,
    },
    {
      title: "Clinic Days",
      dataIndex: "clinicDay",
      key: "clinicDay",
      filteredValue: filteredInfo.clinicDay || null,
      onFilter: (value, record) => record.clinicDay?.includes(value),
      filters: clinicDays.map(d => ({ text: d.name, value: d.name })),
      render: (_, record) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {record.clinicDay?.map((day, i) => (
            <span key={i} className="dl-day-chip">{day}</span>
          ))}
        </div>
      ),
    },
    {
      title: "Hours",
      dataIndex: "appointmentTime",
      key: "appointmentTime",
      render: (_, record) => (
        <Text style={{ fontSize: 12, color: SLATE_MID }}>
          {record.appointmentTime?.[0] || "—"} – {record.appointmentTime?.[1] || "—"}
        </Text>
      ),
    },
    {
      title: "Fee (₦)",
      dataIndex: "feePerConsultation",
      key: "feePerConsultation",
      sorter: (a, b) => Number(a.feePerConsultation) - Number(b.feePerConsultation),
      sortOrder: sortedInfo.columnKey === "feePerConsultation" ? sortedInfo.order : null,
      render: text => <Text strong style={{ fontSize: 13, color: SLATE }}>{text ? `₦${text}` : "—"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filteredValue: filteredInfo.status || null,
      filters: [
        { text: "Approved", value: "approved" },
        { text: "Pending",  value: "pending"  },
        { text: "Disabled", value: "disabled" },
        { text: "Blocked",  value: "blocked"  },
      ],
      onFilter: (value, record) => record.status === value,
      render: status => (
        <span className={`dl-badge dl-badge-${status}`}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {record.status === "pending" && (
            <Button className="dl-action-btn dl-action-approve" icon={<CheckCircleOutlined />}
              onClick={() => changeDoctorStatus(record, "approved")}>
              Approve
            </Button>
          )}
          {(record.status === "approved" || record.status !== "approved" && record.status !== "disabled") && (
            <Button className="dl-action-btn dl-action-edit" icon={<EditOutlined />}
              onClick={() => openEditModal(record)}>
              Edit
            </Button>
          )}
          {record.status === "disabled" && (
            <Button className="dl-action-btn dl-action-enable" icon={<CheckCircleOutlined />}
              onClick={() => changeDoctorStatus(record, "approved")}>
              Enable
            </Button>
          )}
        </div>
      ),
    },
  ];

  const filteredDoctors = doctors
    .filter(d => {
      if (!searchTerm) return true;
      return `${d.firstName} ${d.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .map(d => ({ ...d, key: d._id }));

  return (
    <Layout>
      <div className="dl-root">
        <div className="dl-wrap">

          {/* Hero */}
          <div className="dl-hero dl-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="dl-eyebrow"><TeamOutlined style={{ marginRight: 5 }} />Administration</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Doctors List
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Manage doctor profiles, clinic schedules, and approval statuses
              </Text>
              <div>
                <div className="dl-hero-badge">
                  <UserOutlined style={{ marginRight: 5 }} />{doctors.length} Doctor{doctors.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="dl-toolbar dl-fade dl-fade-1">
            <Input
              className="dl-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search by doctor name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
            <Button className="dl-btn-outline" icon={<FilterOutlined />} onClick={() => setFilteredInfo({})}>
              Clear Filters
            </Button>
            <Button className="dl-btn-outline" icon={<ClearOutlined />} onClick={() => { setFilteredInfo({}); setSortedInfo({}); }}>
              Clear All
            </Button>
          </div>

          {/* Table */}
          <div className="dl-table-wrap dl-fade dl-fade-2">
            <Table
              columns={columns}
              dataSource={filteredDoctors}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 1100 }}
              onChange={handleChange}
            />
          </div>

          {/* Edit Modal */}
          <Modal
            className="dl-modal"
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: TEAL_LIGHT, color: EMERALD, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <EditOutlined />
                </div>
                <span>Update Doctor Profile</span>
              </div>
            }
            open={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            footer={[
              <Button key="cancel" className="dl-modal-cancel" onClick={() => setIsModalVisible(false)} disabled={modalLoading}>
                Cancel
              </Button>,
              <Button key="submit" className="dl-modal-update" loading={modalLoading} onClick={handleEditSubmit}>
                Save Changes
              </Button>,
            ]}
            width={600}
            destroyOnClose
          >
            {/* Status strip */}
            {selectedDoctor && (
              <div className={`dl-status-strip ${selectedDoctor.status !== "disabled" ? "active" : "inactive"}`}>
                <div>
                  <Text strong style={{ fontSize: 13, color: selectedDoctor.status !== "disabled" ? EMERALD : "#dc2626" }}>
                    {selectedDoctor.status !== "disabled" ? "Doctor is currently active" : "Doctor is currently disabled"}
                  </Text>
                  <Text style={{ display: "block", fontSize: 11, color: selectedDoctor.status !== "disabled" ? TEAL : "#ef4444", marginTop: 2 }}>
                    {selectedDoctor.firstName} {selectedDoctor.lastName}
                  </Text>
                </div>
                {selectedDoctor.status !== "disabled" ? (
                  <Button className="dl-strip-btn disable" onClick={handleDisable} icon={<StopOutlined />}>
                    Disable
                  </Button>
                ) : (
                  <Button className="dl-strip-btn enable" onClick={handleEnable} icon={<CheckCircleOutlined />}>
                    Enable
                  </Button>
                )}
              </div>
            )}

            <Form form={form} layout="vertical" requiredMark={false} preserve={false}>
              {/* Doctor name (read-only) */}
              <Form.Item label="Doctor Name">
                <Input
                  value={`${selectedDoctor?.firstName || ""} ${selectedDoctor?.lastName || ""}`}
                  disabled prefix={<UserOutlined style={{ color: MUTED }} />}
                />
              </Form.Item>

              {/* Service */}
              <Form.Item label="Appointment Service" name="service" rules={[{ required: true, message: "Please select a service" }]}>
                <Select placeholder="Select a service" suffixIcon={<MedicineBoxOutlined style={{ color: MUTED }} />}>
                  {services.map(s => <Option key={s.service} value={s.service}>{s.service}</Option>)}
                </Select>
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Email Address" name="email">
                    <Input disabled prefix={<MailOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Phone Number" name="phoneNumber" rules={[{ required: true, message: "Please enter phone number" }]}>
                    <Input placeholder="Phone number" prefix={<PhoneOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Appointment Location" name="location" rules={[{ required: true, message: "Please select location" }]}>
                    <Select placeholder="Select a location" suffixIcon={<EnvironmentOutlined style={{ color: MUTED }} />}>
                      {locations.map(l => <Option key={l.location} value={l.location}>{l.location}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Clinic Days" name="clinicDay" rules={[{ required: true, message: "Please select at least one clinic day" }]}>
                    <Select mode="multiple" allowClear placeholder="Select clinic days">
                      {clinicDays.map(d => <Option key={d.name} value={d.name}>{d.name}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Consultation Fee (₦)" name="feePerConsultation" rules={[{ required: true, message: "Please enter consultation fee" }]}>
                    <Input placeholder="e.g. 5000" prefix={<MoneyCollectFilled style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Appointment Hours" name="appointmentTime" rules={[{ required: true, message: "Please select appointment time" }]}>
                    <TimePicker.RangePicker format="HH:mm" style={{ width: "100%" }} suffixIcon={<ClockCircleOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>

        </div>
      </div>
    </Layout>
  );
}

export default Doctorslist;